import {
  ActionIcon,
  Badge,
  Button,
  Center,
  Divider,
  Flex,
  Group,
  Input,
  Loader,
  Modal,
  MultiSelect,
  NumberInput,
  Pagination,
  ScrollArea,
  Select,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { Edit, Pencil } from "tabler-icons-react";
import * as XLSX from "xlsx";
import {
  useGetAllElevesByParentQuery,
  useGetAllElevesQuery,
} from "../../redux/features/eleveApi";
import {
  parentApi,
  useDeconvertirProspectMutation,
  useEditParentsMutation,
  useGetProspectsQuery,
} from "../../redux/features/parentApi";
import { useGetVillesQuery } from "../../redux/features/villeApi";
import EditAction from "../eleve/actions/EditAction";
import DetailsParent from "./DetailsParent";

const CATEGORY_LABELS = {
  CONTACT: "Contact",
  PROSPECT: "Prospect",
  USER: "Utilisateurs",
  USER_ACTIVE: "Utilisateurs activés",
  CLIENT: "Client",
  CLIENT_ACTIF: "Client actif",
  CLIENT_ENGAGE: "Client engagé",
  CLIENT_IMPACTE: "Client impacté 40j",
};

const CATEGORY_COLORS = {
  CONTACT: "blue",
  PROSPECT: "yellow",
  USER: "green",
  USER_ACTIVE: "violet",
  CLIENT: "red",
  CLIENT_ACTIF: "orange",
  CLIENT_ENGAGE: "teal",
  CLIENT_IMPACTE: "indigo",
};

const COLUMNS_BY_TYPE = {
  CONTACT: [
    "parent",
    "phone",
    "provenance",
    "agent",
    "date",
    "firstLogin",
    "credits",
    "actions",
  ],
  PROSPECT: [
    "parent",
    "phone",
    "enfants",
    "classes",
    "provenance",
    "agent",
    "date",
    "credits",
    "actions",
  ],
  USER: [
    "parent",
    "phone",
    "enfants",
    "classes",
    "sessions",
    "provenance",
    "date",
    "credits",
    "lastUsage",
    "convertir",
    "actions",
  ],
  USER_ACTIVE: [
    "parent",
    "phone",
    "enfants",
    "classes",
    "sessions",
    "provenance",
    "date",
    "credits",
    "lastUsage",
    "convertir",
    "actions",
  ],
  CLIENT: [
    "parent",
    "phone",
    "enfants",
    "classes",
    "sessions",
    "subscription",
    "provenance",
    "dateConversion",
    "credits",
    "deconvertir",
    "actions",
  ],
  CLIENT_ACTIF: [
    "parent",
    "phone",
    "enfants",
    "classes",
    "sessions",
    "sessions7j",
    "subscription",
    "dateConversion",
    "credits",
    "deconvertir",
    "actions",
  ],
  CLIENT_ENGAGE: [
    "parent",
    "phone",
    "enfants",
    "classes",
    "sessions",
    "sessions30j",
    "subscription",
    "dateConversion",
    "credits",
    "deconvertir",
    "actions",
  ],
  CLIENT_IMPACTE: [
    "parent",
    "phone",
    "enfants",
    "classes",
    "sessions",
    "subscription",
    "progression",
    "impact",
    "dateConversion",
    "credits",
    "deconvertir",
    "actions",
  ],
};

const COLUMN_HEADERS = {
  parent: "Parent",
  phone: "Téléphone",
  enfants: "Enfants",
  classes: "Classes",
  sessions: "Sessions",
  sessions7j: "Sessions (7j)",
  sessions30j: "Sessions (30j)",
  provenance: "Provenance",
  agent: "Agent commercial",
  date: "Date de création",
  firstLogin: "1ère Connexion",
  dateConversion: "Date de conversion",
  credits: "Crédits",
  lastUsage: "Dernière utilisation",
  subscription: "Abonnement",
  progression: "Progression",
  impact: "Impacté",
  convertir: "Convertir",
  deconvertir: "Déconvertir",
  actions: "Actions",
};

const extractClassName = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value;
  return (
    value?.name ||
    value?.sub_title ||
    value?.title ||
    value?.label ||
    value?.classe ||
    value?.niveau ||
    null
  );
};

const formatClassesFromChildren = (children) => {
  if (!Array.isArray(children)) return "";
  const classNames = new Set();

  children.forEach((child) => {
    const values = [
      child?.classe,
      child?.classe_name,
      child?.classeName,
      child?.niveau,
      child?.niveau_name,
    ];
    values.forEach((value) => {
      const name = extractClassName(value);
      if (name) classNames.add(name);
    });
  });

  return Array.from(classNames).filter(Boolean).join(", ");
};

const formatClassesList = (data) => {
  const classNames = new Set();

  const pushClass = (value) => {
    const name = extractClassName(value);
    if (name) classNames.add(name);
  };

  const directLists = [
    data?.classes,
    data?.classes_list,
    data?.classList,
    data?.niveaux,
  ];

  directLists.forEach((list) => {
    if (Array.isArray(list)) {
      list.forEach((item) => pushClass(item));
    }
  });

  const childrenLists = [
    data?.enfants,
    data?.children,
    data?.eleves,
    data?.students,
    data?.parent?.enfants,
    data?.parent?.eleves,
  ];

  childrenLists.forEach((list) => {
    if (Array.isArray(list)) {
      list.forEach((child) => {
        pushClass(child?.classe);
        pushClass(child?.classe_name);
        pushClass(child?.classeName);
        pushClass(child?.niveau);
        pushClass(child?.niveau_name);
      });
    }
  });

  const formatted = Array.from(classNames).filter(Boolean);
  return formatted.length > 0 ? formatted.join(", ") : "—";
};

const ClassesCell = ({ parentId, countEnfant, fallback }) => {
  const shouldFetch =
    Boolean(parentId) &&
    (countEnfant ?? 0) > 0 &&
    (!fallback || fallback === "—");

  const { data, isLoading } = useGetAllElevesByParentQuery(parentId, {
    skip: !shouldFetch,
  });

  const classesFromApi = formatClassesFromChildren(data?.data);

  if (fallback && fallback !== "—") return fallback;
  if (!shouldFetch) return fallback || "—";
  if (isLoading) {
    return (
      <Text size="xs" color="dimmed">
        Chargement...
      </Text>
    );
  }
  return classesFromApi || "—";
};

function ProspectPage({ type }) {
  const [query, setQuery] = useState("");
  const [provenance, setProvenance] = useState("");
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startActivityDate, setStartActivityDate] = useState(null);
  const [endActivityDate, setEndActivityDate] = useState(null);
  const [startConversionDate, setStartConversionDate] = useState(null);
  const [endConversionDate, setEndConversionDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // Déterminer si c'est un onglet client
  const isClientTab = [
    "CLIENT",
    "CLIENT_ACTIF",
    "CLIENT_ENGAGE",
    "CLIENT_IMPACTE",
  ].includes(type);

  const hasFilters =
    query ||
    provenance ||
    selectedClasses.length > 0 ||
    startDate ||
    endDate ||
    startActivityDate ||
    endActivityDate ||
    startConversionDate ||
    endConversionDate;

  const {
    data: prospectsData,
    isLoading,
    isFetching,
    refetch,
  } = useGetProspectsQuery({
    category: type,
    page: hasFilters ? 1 : currentPage,
    limit: hasFilters ? 10000 : itemsPerPage,
  });

  const { data: elevesData } = useGetAllElevesQuery({ limit: 50000 });

  const dispatch = useDispatch();
  const [opened, { open, close }] = useDisclosure(false);
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] =
    useDisclosure(false);
  const [editProspect, setEditProspect] = useState({ id: null, credits: null });
  const [editProspectInfo, setEditProspectInfo] = useState(null);
  const [editProspectCredit, { isLoading: isCreditLoading }] =
    useEditParentsMutation();
  const [editProspectMutation, { isLoading: isEditLoading }] =
    useEditParentsMutation();
  const [deconvertirProspect] = useDeconvertirProspectMutation();
  const { data: villes } = useGetVillesQuery();

  const dataArray = Array.isArray(prospectsData?.data)
    ? prospectsData.data
    : Array.isArray(prospectsData)
      ? prospectsData
      : [];

  const { classOptions, classesByParentId } = useMemo(() => {
    const students = Array.isArray(elevesData?.data)
      ? elevesData.data
      : Array.isArray(elevesData)
        ? elevesData
        : [];
    const classSet = new Set();
    const classMap = new Map();

    students.forEach((student) => {
      const className =
        extractClassName(student?.classe) ||
        extractClassName(student?.classe_name) ||
        extractClassName(student?.classeName) ||
        extractClassName(student?.niveau) ||
        extractClassName(student?.niveau_name);

      if (className) {
        classSet.add(className);
      }

      const parentId =
        student?.parent?.id ||
        student?.parentId ||
        student?.parrentId ||
        student?.parent_id ||
        student?.parentID ||
        student?.parent?.parentId ||
        student?.parent?.parent_id;

      if (parentId && className) {
        if (!classMap.has(parentId)) classMap.set(parentId, new Set());
        classMap.get(parentId).add(className);
      }
    });

    return {
      classOptions: Array.from(classSet)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }))
        .map((value) => ({ value, label: value })),
      classesByParentId: classMap,
    };
  }, [elevesData]);

  const getParentClassesForFilter = (parent) => {
    if (!parent) return [];
    const mapped = classesByParentId.get(parent.id);
    if (mapped && mapped.size > 0) return Array.from(mapped);

    const fallback = formatClassesList(parent);
    if (!fallback || fallback === "—") return [];
    return fallback
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
  };

  const hanldeConvert = async (id) => {
    dispatch(parentApi.endpoints.convertirProspect.initiate(id))
      .unwrap()
      .then(() => {
        toast.success("Prospect converti avec succès");
        refetch();
      })
      .catch((error) => toast.error(error?.data?.message));
  };

  const handleDeconvert = (data) => {
    modals.openConfirmModal({
      title: "Déconvertir ce client",
      centered: true,
      children: (
        <Text size="sm">
          Voulez-vous vraiment déconvertir <strong>{data?.fullName}</strong>{" "}
          ? Cette action n'est pas recommandée.
        </Text>
      ),
      labels: { confirm: "Déconvertir", cancel: "Annuler" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          await deconvertirProspect({
            id: data?.id,
            stageBeforeClient: data?.stage_before_client,
          }).unwrap();
          toast.success("Client déconverti avec succès");
          refetch();
        } catch (error) {
          toast.error(
            error?.data?.message ||
              "Un problème est survenu lors de la déconversion",
          );
        }
      },
    });
  };

  const form = useForm({
    validate: {
      credits: (value) =>
        !isNaN(parseFloat(value)) && isFinite(value) && parseFloat(value) >= 0
          ? null
          : "Nombre invalide",
    },
  });

  const editForm = useForm({
    initialValues: {
      fullName: "",
      phone: "",
      adresse: "",
      villeId: "",
      provenance: "",
      agentCommercialFullName: "",
    },
    validate: {
      fullName: (value) => (value ? null : "Le nom est requis"),
      phone: (value) => (value ? null : "Le téléphone est requis"),
    },
  });

  const handleChangeCredits = async (e) => {
    const body = {
      id: parseInt(editProspect?.id),
      credits: parseInt(e?.credits),
    };
    const res = await EditAction(editProspectCredit, body);
    if (res?.data?.status === "success") {
      setEditProspect({ id: null, credits: null });
      form.reset();
      toast.success("Modification crédits réussie");
      close();
      refetch();
      return;
    }
    return toast.error(
      "Un problème est survenu, veuillez contacter l'équipe technique",
    );
  };

  const handleOpenEditModal = (prospect) => {
    setEditProspectInfo(prospect);
    editForm.setValues({
      fullName: prospect?.fullName || "",
      phone: prospect?.phone || "",
      adresse: prospect?.adresse || "",
      villeId: prospect?.villeId?.toString() || "",
      provenance: prospect?.provenance || "",
      agentCommercialFullName: prospect?.agentCommercialFullName || "",
    });
    openEditModal();
  };

  const handleEditProspect = async (values) => {
    const body = {
      id: parseInt(editProspectInfo?.id),
      fullName: values.fullName,
      phone: values.phone,
      adresse: values.adresse,
      villeId: values.villeId ? parseInt(values.villeId) : null,
      provenance: values.provenance,
      agentCommercialFullName: values.agentCommercialFullName,
    };

    const res = await EditAction(editProspectMutation, body);
    if (res?.data?.status === "success") {
      setEditProspectInfo(null);
      editForm.reset();
      toast.success("Prospect modifié avec succès");
      closeEditModal();
      refetch();
      return;
    }
    return toast.error(
      "Un problème est survenu, veuillez contacter l'équipe technique",
    );
  };

  const selectedClassSet = selectedClasses
    .map((value) => value.toLowerCase())
    .filter(Boolean);

  // Filtrage local (recherche texte, provenance, date de création, date d'activité, date de conversion)
  const filtered = dataArray.filter((data) => {
    const nameMatch = data?.fullName?.toLowerCase().includes(query);
    const phoneMatch = data?.phone?.includes(query);
    const provenanceFilter =
      !provenance ||
      data?.provenance?.toLowerCase() === provenance?.toLowerCase();
    const classFilter =
      selectedClassSet.length === 0 ||
      getParentClassesForFilter(data).some((cls) =>
        selectedClassSet.includes(cls.toLowerCase()),
      );

    let creationDateFilter = true;
    if ((startDate || endDate) && data?.created_at) {
      const createdAt = new Date(data.created_at);
      if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        if (createdAt < start) creationDateFilter = false;
      }
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (createdAt > end) creationDateFilter = false;
      }
    }

    let activityDateFilter = true;
    if (startActivityDate || endActivityDate) {
      if (!data?.last_credit_usage) {
        activityDateFilter = false;
      } else {
        const lastUsageAt = new Date(data.last_credit_usage);
        if (startActivityDate) {
          const start = new Date(startActivityDate);
          start.setHours(0, 0, 0, 0);
          if (lastUsageAt < start) activityDateFilter = false;
        }
        if (endActivityDate) {
          const end = new Date(endActivityDate);
          end.setHours(23, 59, 59, 999);
          if (lastUsageAt > end) activityDateFilter = false;
        }
      }
    }

    let conversionDateFilter = true;
    if (startConversionDate || endConversionDate) {
      if (!data?.date_conversion) {
        conversionDateFilter = false;
      } else {
        const conversionAt = new Date(data.date_conversion);
        if (startConversionDate) {
          const start = new Date(startConversionDate);
          start.setHours(0, 0, 0, 0);
          if (conversionAt < start) conversionDateFilter = false;
        }
        if (endConversionDate) {
          const end = new Date(endConversionDate);
          end.setHours(23, 59, 59, 999);
          if (conversionAt > end) conversionDateFilter = false;
        }
      }
    }

    return (
      (nameMatch || phoneMatch) &&
      provenanceFilter &&
      classFilter &&
      creationDateFilter &&
      activityDateFilter &&
      conversionDateFilter
    );
  });

  const formatLastCreditUsage = (dateString) => {
    if (!dateString) return "Jamais";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const totalCount = prospectsData?.total_count || 0;
  const totalPages = hasFilters
    ? Math.ceil(filtered.length / itemsPerPage)
    : Math.ceil(totalCount / itemsPerPage);

  const paginatedData = hasFilters
    ? filtered.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage,
      )
    : filtered;

  useEffect(() => {
    setCurrentPage(1);
  }, [
    query,
    provenance,
    startDate,
    endDate,
    startActivityDate,
    endActivityDate,
    startConversionDate,
    endConversionDate,
    selectedClasses,
    type,
  ]);

  // Réinitialiser les filtres de conversion quand on quitte les onglets clients
  useEffect(() => {
    if (!isClientTab) {
      setStartConversionDate(null);
      setEndConversionDate(null);
    }
  }, [isClientTab]);

  const columns = COLUMNS_BY_TYPE[type] || COLUMNS_BY_TYPE.CONTACT;

  const renderCell = (col, data) => {
    switch (col) {
      case "parent":
        return data?.fullName;
      case "phone":
        return data?.phone;
      case "enfants":
        return data?.countEnfant;
      case "classes":
        return (
          <ClassesCell
            parentId={data?.id}
            countEnfant={data?.countEnfant}
            fallback={formatClassesList(data)}
          />
        );
      case "sessions":
        return data?.nb_sessions_total ?? 0;
      case "sessions7j":
        return data?.nb_sessions_last_7_days ?? 0;
      case "sessions30j":
        return data?.nb_sessions_last_30_days ?? 0;
      case "provenance":
        return data?.provenance;
      case "agent":
        return data?.agentCommercialFullName;
      case "date":
        return data?.created_at
          ? new Date(data.created_at).toLocaleDateString("fr-FR")
          : "";
      case "firstLogin":
        const firstLoginStr = data?.first_login || data?.first_connexion || data?.date_premier_login;
        return firstLoginStr ? new Date(firstLoginStr).toLocaleDateString("fr-FR") : "—";
      case "dateConversion":
        return data?.date_conversion
          ? new Date(data.date_conversion).toLocaleDateString("fr-FR")
          : "—";
      case "credits":
        return (
          <Group spacing={4} noWrap>
            {data?.credits}
            <ActionIcon
              size="xs"
              onClick={() => {
                setEditProspect({ id: data?.id, credits: data?.credits });
                open();
              }}
            >
              <Edit color="orange" size={14} />
            </ActionIcon>
          </Group>
        );
      case "lastUsage":
        return formatLastCreditUsage(data?.last_credit_usage);
      case "subscription":
        return (
          <Badge
            color={data?.subscription_status === "active" ? "green" : "gray"}
            size="sm"
            variant="filled"
          >
            {data?.subscription_status || "inactive"}
          </Badge>
        );
      case "progression":
        return data?.progression_score != null
          ? `${data.progression_score.toFixed(1)}%`
          : "—";
      case "impact":
        return (
          <Badge
            color={data?.impact_status ? "green" : "gray"}
            size="sm"
            variant="filled"
          >
            {data?.impact_status ? "Oui" : "Non"}
          </Badge>
        );
      case "convertir":
        return (
          <Button
            size="xs"
            color="blue"
            variant="light"
            onClick={() => hanldeConvert(data?.id)}
            disabled={data?.type === "CONFIRMED"}
          >
            {data?.type === "CONFIRMED" ? "Converti" : "Convertir"}
          </Button>
        );
      case "deconvertir":
        return (
          <Button
            size="xs"
            color="red"
            variant="filled"
            radius="xl"
            onClick={() => handleDeconvert(data)}
          >
            Déconvertir
          </Button>
        );
      case "actions":
        return (
          <Group spacing={5} noWrap>
            <ActionIcon
              color="green"
              onClick={() => handleOpenEditModal(data)}
              title="Modifier les informations"
            >
              <Pencil size={16} />
            </ActionIcon>
            <DetailsParent
              parent={{ id: data?.id, fullName: data?.fullName }}
            />
          </Group>
        );
      default:
        return null;
    }
  };

  const rows = paginatedData.map((data) => (
    <tr key={data.id}>
      {columns.map((col) => (
        <td key={col}>{renderCell(col, data)}</td>
      ))}
    </tr>
  ));

  const TableBody = () => {
    if (!rows || rows.length === 0) {
      return (
        <tr>
          <td colSpan={columns.length} style={{ textAlign: "center" }}>
            Pas de données
          </td>
        </tr>
      );
    }
    return rows;
  };

  const handleExport = () => {
    const headers = columns.map((col) => COLUMN_HEADERS[col] || col);
    const exportRows = filtered.map((d) =>
      columns.map((col) => {
        switch (col) {
          case "parent":
            return d?.fullName ?? "";
          case "phone":
            return d?.phone ?? "";
          case "enfants":
            return d?.countEnfant ?? "";
          case "classes":
            return formatClassesList(d);
          case "sessions":
            return d?.nb_sessions_total ?? 0;
          case "sessions7j":
            return d?.nb_sessions_last_7_days ?? 0;
          case "sessions30j":
            return d?.nb_sessions_last_30_days ?? 0;
          case "provenance":
            return d?.provenance ?? "";
          case "agent":
            return d?.agentCommercialFullName ?? "";
          case "date":
            return d?.created_at
              ? new Date(d.created_at).toLocaleDateString("fr-FR")
              : "";
          case "dateConversion":
            return d?.date_conversion
              ? new Date(d.date_conversion).toLocaleDateString("fr-FR")
              : "—";
          case "credits":
            return d?.credits ?? "";
          case "lastUsage":
            return d?.last_credit_usage
              ? new Date(d.last_credit_usage).toLocaleDateString("fr-FR")
              : "Jamais";
          case "subscription":
            return d?.subscription_status ?? "inactive";
          case "progression":
            return d?.progression_score ?? "";
          case "impact":
            return d?.impact_status ? "Oui" : "Non";
          case "actions":
            return "";
          default:
            return "";
        }
      }),
    );
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...exportRows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Export");
    const dateStr = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(
      workbook,
      `export_${(CATEGORY_LABELS[type] || type).toLowerCase().replace(/ /g, "_")}_${dateStr}.xlsx`,
    );
  };

  return (
    <div>
      <Flex justify="space-between" align="center" gap="sm" wrap="wrap">
        <Input
          placeholder="Rechercher par nom ou téléphone"
          onChange={(e) => setQuery(e.target.value.toLowerCase())}
          value={query}
          style={{ minWidth: 200 }}
        />
        <DatePickerInput
          placeholder="Création du"
          value={startDate}
          onChange={setStartDate}
          clearable
          style={{ minWidth: 140 }}
        />
        <DatePickerInput
          placeholder="Création au"
          value={endDate}
          onChange={setEndDate}
          clearable
          style={{ minWidth: 140 }}
        />
        <DatePickerInput
          placeholder="Activité du"
          value={startActivityDate}
          onChange={setStartActivityDate}
          clearable
          style={{ minWidth: 140 }}
        />
        <DatePickerInput
          placeholder="Activité au"
          value={endActivityDate}
          onChange={setEndActivityDate}
          clearable
          style={{ minWidth: 140 }}
        />
        {isClientTab && (
          <>
            <DatePickerInput
              placeholder="Conversion du"
              value={startConversionDate}
              onChange={setStartConversionDate}
              clearable
              style={{ minWidth: 140 }}
            />
            <DatePickerInput
              placeholder="Conversion au"
              value={endConversionDate}
              onChange={setEndConversionDate}
              clearable
              style={{ minWidth: 140 }}
            />
          </>
        )}
        <Select
          placeholder="Filtrer par provenance"
          data={["GENIMI", "SENASSURANCE"]}
          value={provenance}
          onChange={(e) => setProvenance(e || "")}
          clearable
        />
        <MultiSelect
          placeholder="Filtrer par classes"
          data={classOptions}
          value={selectedClasses}
          onChange={setSelectedClasses}
          clearable
          searchable
          maxDropdownHeight={220}
          nothingFound="Aucune classe"
          style={{ minWidth: 220 }}
        />
        <Badge color={CATEGORY_COLORS[type]} size="lg" variant="filled">
          {CATEGORY_LABELS[type] || type}
        </Badge>
        <Button variant="outline" onClick={handleExport}>
          Exporter (XLSX)
        </Button>
      </Flex>

      {hasFilters && (
        <Text size="sm" color="dimmed" mt="xs">
          {filtered.length} résultat{filtered.length > 1 ? "s" : ""} trouvé
          {filtered.length > 1 ? "s" : ""} sur {totalCount} au total
        </Text>
      )}

      <Divider my={10} />
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <ScrollArea style={{ position: "relative" }}>
            {isFetching && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10,
                }}
              >
                <Loader />
              </div>
            )}
            <Table
              horizontalSpacing="md"
              verticalSpacing="xs"
              miw={1124}
              sx={{ tableLayout: "fixed" }}
              bg="#fff"
            >
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th key={col}>{COLUMN_HEADERS[col] || col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <TableBody />
              </tbody>
            </Table>
          </ScrollArea>

          {!isLoading && filtered.length > 0 && (
            <Center mt="xl" mb="xl">
              <Pagination
                total={totalPages}
                value={currentPage}
                onChange={setCurrentPage}
                size="md"
                radius="md"
                disabled={isFetching}
              />
            </Center>
          )}
        </>
      )}

      {/* Modal crédits */}
      <Modal
        opened={opened}
        onClose={close}
        title="Modification crédits"
        size="md"
      >
        <form onSubmit={form.onSubmit((values) => handleChangeCredits(values))}>
          <NumberInput
            placeholder="Mettez le nombre de crédits"
            {...form.getInputProps("credits")}
            defaultValue={editProspect.credits}
          />
          <Group mt={10}>
            <Button
              color="gray"
              onClick={() => {
                form.reset();
                close();
              }}
            >
              Annuler
            </Button>
            <Button type="submit">
              {isCreditLoading ? "Envoi..." : "Modifier"}
            </Button>
          </Group>
        </form>
      </Modal>

      {/* Modal édition informations */}
      <Modal
        opened={editModalOpened}
        onClose={() => {
          editForm.reset();
          closeEditModal();
        }}
        title="Modifier les informations du prospect"
        size="md"
      >
        <form
          onSubmit={editForm.onSubmit((values) => handleEditProspect(values))}
        >
          <TextInput
            label="Nom complet"
            placeholder="Nom du prospect"
            {...editForm.getInputProps("fullName")}
            mb={10}
          />
          <TextInput
            label="Téléphone"
            placeholder="77 *** ** **"
            {...editForm.getInputProps("phone")}
            mb={10}
          />
          <TextInput
            label="Adresse"
            placeholder="Adresse"
            {...editForm.getInputProps("adresse")}
            mb={10}
          />
          <Select
            label="Ville"
            placeholder="Sélectionner une ville"
            data={
              villes?.data?.map((item) => ({
                value: item?.id?.toString(),
                label: item?.name,
              })) ?? []
            }
            {...editForm.getInputProps("villeId")}
            clearable
            mb={10}
          />
          <Select
            label="Provenance"
            placeholder="Sélectionner la provenance"
            data={["GENIMI", "SENASSURANCE"]}
            {...editForm.getInputProps("provenance")}
            clearable
            mb={10}
          />
          <TextInput
            label="Agent commercial"
            placeholder="Nom de l'agent commercial"
            {...editForm.getInputProps("agentCommercialFullName")}
            mb={10}
          />
          <Group mt={15} position="right">
            <Button
              color="gray"
              variant="outline"
              onClick={() => {
                editForm.reset();
                closeEditModal();
              }}
            >
              Annuler
            </Button>
            <Button type="submit" loading={isEditLoading}>
              Modifier
            </Button>
          </Group>
        </form>
      </Modal>
    </div>
  );
}

export default ProspectPage;
